import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'

import CloseIcon from '@mui/icons-material/Close'

import {
    useGetNotificationPreferencesQuery,
    useUpdateNotificationPreferencesMutation,
    useSendTestNotificationMutation
} from '@/store/api/notificationsApi'

// Etiqueta legible por tipo/categoría. Un tipo nuevo añadido en el backend
// (utils/notificationTypes.js) sin entrada aquí simplemente muestra el
// nombre crudo — no rompe nada, es solo menos bonito hasta traducirlo.
const TYPE_LABELS = {
    NODE_OFFLINE: 'Node offline',
    NODE_UPDATE_ROLLBACK: 'Update rollback',
    NODE_CRASH_LOOP: 'Crash loop',
    NODE_CRASHED: 'Node crashed',
    NODE_COMMAND_FAILED: 'Command execution failed',
    ALARM_DETECTED: 'Book alert detected',
    SIP_OPERATION_SUCCESS: 'SIP operation succeeded',
    SIP_OPERATION_WARNING: 'SIP operation warning',
    SIP_OPERATION_ERROR: 'SIP operation failed',
    INVENTORY_CREATED_WEB: 'Inventory created from web',
    INVENTORY_CREATED_DEVICE: 'Inventory created from scan device',
    SIGNAL_STATE_CHANGED: 'Signal state changed',
    OCCUPANCY_LEVEL_CHANGED: 'Occupancy level changed',
    TEST: 'Test notification'
}

const CATEGORY_LABELS = {
    nodes: 'Nodes',
    alarms: 'Book Alerts',
    sip: 'SIP Operations',
    inventories: 'Inventories',
    signals: 'Signals',
    occupancy: 'Occupancy',
    system: 'System'
}

// Un tipo de notificación se resume en un único modo (en vez de 3 switches
// independientes: enabled/push/email) porque enabled:false invalida los otros
// dos — así no existen estados imposibles como "desactivada pero con push".
// Cada modo mapea 1:1 a la combinación de booleans que espera el backend.
const MODES = [
    { value: 'off', enabled: false, push: false, email: false },
    { value: 'bell', enabled: true, push: false, email: false },
    { value: 'push', enabled: true, push: true, email: false },
    { value: 'email', enabled: true, push: false, email: true },
    { value: 'both', enabled: true, push: true, email: true }
]

const MODE_LABELS = {
    off: 'Disabled',
    bell: 'Enabled',
    push: 'Push',
    email: 'Email',
    both: 'Push + Email'
}

const modeOf = (item) => {
    if (!item.enabled) return 'off'
    if (item.push && item.email) return 'both'
    if (item.push) return 'push'
    if (item.email) return 'email'
    return 'bell'
}

const NotificationPreferencesDialog = ({ open, onClose }) => {
    const { t } = useTranslation()
    const { data, isFetching } = useGetNotificationPreferencesQuery(undefined, { skip: !open })
    const [updatePreferences, { isLoading: isSaving }] = useUpdateNotificationPreferencesMutation()
    const [sendTest, { isLoading: isSendingTest }] = useSendTestNotificationMutation()

    const [items, setItems] = useState([])

    useEffect(() => {
        if (data?.items) {
            setItems(data.items)
        }
    }, [data])

    const setMode = (type, mode) => {
        const { enabled, push, email } = MODES.find((m) => m.value === mode)
        setItems((prev) =>
            prev.map((item) => (item.type === type ? { ...item, enabled, push, email } : item))
        )
    }

    const handleSave = async () => {
        await updatePreferences(
            items.map(({ type, enabled, push, email }) => ({ type, enabled, push, email }))
        ).unwrap()
        onClose()
    }

    const grouped = items.reduce((acc, item) => {
        acc[item.category] = acc[item.category] || []
        acc[item.category].push(item)
        return acc
    }, {})

    // TEST es minRole:'admin' — si el backend no lo devuelve en items es que
    // el usuario actual no es admin, así que se oculta el botón de prueba.
    const canSendTest = items.some((item) => item.type === 'TEST')

    return (
        <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                {t('Notification settings')}
                <IconButton
                    onClick={onClose}
                    size='small'
                    sx={{ position: 'absolute', right: 12, top: 12 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {isFetching ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <Stack spacing={3}>
                        <Typography variant='body2' color='text.secondary'>
                            {t(
                                'Choose whether you want to receive each notification type, and if so, whether as bell only, push, email, or both. Disabling a type also hides it from the bell.'
                            )}
                        </Typography>

                        {Object.entries(grouped).map(([category, categoryItems]) => (
                            <Stack key={category} spacing={1}>
                                <Typography variant='subtitle2'>
                                    {t(CATEGORY_LABELS[category] || category)}
                                </Typography>
                                <Divider />
                                {categoryItems.map((item) => (
                                    <Stack
                                        key={item.type}
                                        direction='row'
                                        sx={{ alignItems: 'center', py: 0.5 }}>
                                        <Typography variant='body2' sx={{ flex: 1 }}>
                                            {t(TYPE_LABELS[item.type] || item.type)}
                                        </Typography>
                                        <FormControl size='small' sx={{ minWidth: 160 }}>
                                            <Select
                                                value={modeOf(item)}
                                                onChange={(e) =>
                                                    setMode(item.type, e.target.value)
                                                }>
                                                {MODES.map(({ value }) => (
                                                    <MenuItem key={value} value={value}>
                                                        {t(MODE_LABELS[value])}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Stack>
                                ))}
                            </Stack>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                {canSendTest && (
                    <Button onClick={() => sendTest()} disabled={isSendingTest}>
                        {t('Send test notification')}
                    </Button>
                )}
                <Box sx={{ flex: 1 }} />
                <Button onClick={onClose}>{t('Close')}</Button>
                <Button variant='contained' onClick={handleSave} disabled={isSaving || isFetching}>
                    {t('Save')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default NotificationPreferencesDialog
